import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Paperclip, Download, Trash2, Loader2, Upload, FileText, ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 10 * 1024 * 1024;

const ACCEPT_MIME = [
    'application/pdf',
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
].join(',');

function formatBytes(n) {
    if (!n) return '0 o';
    if (n < 1024) return `${n} o`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
    return `${(n / 1024 / 1024).toFixed(2)} Mo`;
}

function fileIcon(name = '') {
    const ext = (name.split('.').pop() || '').toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(ext)) return ImageIcon;
    return FileText;
}

/**
 * Attachment manager for a single agenda-demenagement event.
 *
 * Props:
 *   eventId : string (required)
 *
 * Usage: place inside the Event Detail modal.
 */
export default function EventPiecesJointes({ eventId }) {
    const { user } = useAuth();
    const role = user?.role;
    const isPrivileged = role === 'admin' || role === 'responsable';

    const [pieces, setPieces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const fetchPieces = useCallback(async () => {
        if (!eventId) return;
        setLoading(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/agenda-demenagement/event/${eventId}/pieces-jointes`,
                { withCredentials: true }
            );
            setPieces(res.data || []);
        } catch {
            toast.error('Impossible de charger les pièces jointes');
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    useEffect(() => {
        fetchPieces();
    }, [fetchPieces]);

    const readAsBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(file);
        });

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;

        for (const file of files) {
            if (file.size > MAX_BYTES) {
                toast.error(`"${file.name}" dépasse la limite de 10 Mo`);
                continue;
            }
            setUploading(true);
            try {
                const dataUrl = await readAsBase64(file);
                const res = await axios.post(
                    `${API_URL}/api/agenda-demenagement/event/${eventId}/pieces-jointes`,
                    {
                        file_name: file.name,
                        content_type: file.type || '',
                        file_data: dataUrl,
                    },
                    { withCredentials: true }
                );
                setPieces((prev) => [...prev, res.data]);
                toast.success(`"${file.name}" ajouté`);
            } catch (err) {
                toast.error(err?.response?.data?.detail || `Erreur upload "${file.name}"`);
            } finally {
                setUploading(false);
            }
        }
        // reset input value so the same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDownload = async (piece) => {
        try {
            const res = await axios.get(`${API_URL}${piece.url}`, {
                responseType: 'blob',
                withCredentials: true,
            });
            const blob = new Blob([res.data], { type: piece.content_type || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = piece.file_name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 500);
        } catch {
            toast.error('Téléchargement impossible');
        }
    };

    const handleDelete = async (piece) => {
        if (!window.confirm(`Supprimer "${piece.file_name}" ?`)) return;
        try {
            await axios.delete(
                `${API_URL}/api/agenda-demenagement/event/${eventId}/pieces-jointes/${piece.id}`,
                { withCredentials: true }
            );
            setPieces((prev) => prev.filter((p) => p.id !== piece.id));
            toast.success('Fichier supprimé');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur suppression');
        }
    };

    const canDelete = (piece) => isPrivileged || piece.uploaded_by === user?.id;

    return (
        <div className="border border-gray-200 rounded-xl p-4" data-testid="event-pieces-jointes-section">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5" />
                    Pièces jointes {pieces.length > 0 && (
                        <span className="bg-[#1B365D] text-white text-[10px] rounded-full px-1.5 py-0.5">{pieces.length}</span>
                    )}
                </h3>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPT_MIME}
                    onChange={(e) => handleFiles(e.target.files)}
                    className="hidden"
                    data-testid="event-pieces-file-input"
                />
                <Button
                    type="button"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="gap-1.5 bg-[#1B365D] hover:bg-[#2A4A7A] text-white text-xs h-8"
                    data-testid="event-pieces-add-btn"
                >
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    Joindre un fichier
                </Button>
            </div>

            {loading ? (
                <div className="text-center text-gray-400 text-sm py-4">Chargement…</div>
            ) : pieces.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-4 italic">
                    Aucune pièce jointe. Cliquez sur « Joindre un fichier » pour en ajouter.
                </div>
            ) : (
                <ul className="space-y-1.5">
                    {pieces.map((p) => {
                        const Icon = fileIcon(p.file_name);
                        const deletable = canDelete(p);
                        return (
                            <li
                                key={p.id}
                                className="flex items-center gap-2 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg px-3 py-2"
                                data-testid={`event-piece-${p.id}`}
                            >
                                <Icon className="w-4 h-4 text-[#1B365D] shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-800 truncate" title={p.file_name}>
                                        {p.file_name}
                                    </p>
                                    <p className="text-[11px] text-gray-500">
                                        {formatBytes(p.size)} • {p.uploaded_by_name}
                                        {p.uploaded_at ? ` • ${new Date(p.uploaded_at).toLocaleDateString('fr-FR')}` : ''}
                                    </p>
                                </div>
                                <button
                                    onClick={() => handleDownload(p)}
                                    title="Télécharger"
                                    className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                                    data-testid={`event-piece-download-${p.id}`}
                                >
                                    <Download className="w-4 h-4" />
                                </button>
                                {deletable && (
                                    <button
                                        onClick={() => handleDelete(p)}
                                        title="Supprimer"
                                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                        data-testid={`event-piece-delete-${p.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
